import { Button, Flex, Form, Layout, MenuProps, notification, Row, Select, Space, Typography } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import { IOuterTableRef, TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { settingHeightTableWithFilter } from 'src/app/shared/constants/common.constants'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import { colorBgErrorBold, colorBorder, colorText } from 'src/app/themes/primaryThemeDefault'
import { DeleteIcon, EyeIcon, PenIcon, RefreshIcon, WhiteSearchIcon } from 'src/assets/icons'
import DeleteImg from '../../../../../assets/images/delete-img.png'
import DeleteButton from '../../../../shared/components/common/button/delete-button'
import RichTextField from '../../../../shared/components/common/rich-text-field'
import { ShortenListTags } from '../../../../shared/components/common/tag/ShortenListTags'
import ActionConfirmationModal from '../../../../shared/components/modal/action-confirmation-modal'
import { SYSTEM_MESSAGE_IDS } from '../../../../shared/constants/systemMessages.constants'
import {
  bulkDeleteACHExceptionRuleList,
  getACHExceptionRuleList,
  getACHExceptionRuleDownloadStatus,
  getACHExceptionRuleDownloadFile,
} from '../../../../shared/services/ACHExceptionRuleService'
import ACHUploadTemplateModal from '../ach-upload-multiple-template/ACHUploadTemplateModal'
import ErrorDetailsUpload from '../ach-upload-multiple-template/ErrorDetailsUpload'
import './index.scss'
import {
  IReqACHExceptionRulesPayload,
  useACHExceptionRule,
  useClientSelectData,
  useDeleteACHException,
} from './useACHExceptionRule'

export const outerTableRef = React.createRef<Partial<IOuterTableRef>>()

const LandingACHExceptionRules: React.FC = () => {
  const { t } = useTranslation(undefined, { useSuspense: true })
  const navigate = useNavigate()
  const { userPermissions } = useRolePermission()
  const [api, contextHolder] = notification.useNotification()
  const [errorRows, setErrorRows] = useState<any>({})
  const [isOpenUploadModal, setIsOpenUploadModal] = useState<boolean>(false)
  const { ACHExceptionRuleList, totalRecords, isLoading, getACHExceptionRuleListData } = useACHExceptionRule()
  const { clientOptions, getClientOptions } = useClientSelectData()
  const currentPayload = useRef<IReqACHExceptionRulesPayload>()
  const [clientName, setClientName] = useState<string>()
  const [isSearchDropdown, setIsSearchDropdown] = useState<boolean>(false)
  const [isResetView, setIsResetView] = useState(false)
  const uploadModalRef = useRef<{ openModal: Function; closeModal: Function }>()
  const errorDetailsUploadModalRef = useRef<{ openModal: Function; closeModal: Function }>()

  const [isModalConfirmDeleteOpened, setIsModalConfirmDeleteOpened] = useState<boolean>(false)
  const [selectedRows, setSelectedRows] = useState<Array<any>>([])
  const [isConfirmBulkDeletionModalOpened, setIsConfirmBulkDeletionModalOpened] = useState<boolean>(false)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)

  const [selectedACH, setSelectedACH] = useState<any>({})
  const { isLoading: isLoadingDeleteACH, deleteACHException } = useDeleteACHException()

  const getItems = useCallback(
    (rowData: any): MenuProps['items'] => {
      return [
        userPermissions[ESubModules.ACH_EXCEPT_RUL]?.includes(EPermissions.VIEW_ITEMS)
          ? {
              key: '1',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>
                  {t(`ACHExceptionRule:list.label.viewACH`)}
                </Typography.Text>
              ),
              icon: <EyeIcon />,
              onClick: () => {
                if (rowData?.ruleId) navigate(`${menuUrls.achExceptionRulesView}/${rowData?.ruleId}`)
              },
            }
          : null,
        userPermissions[ESubModules.ACH_EXCEPT_RUL]?.includes(EPermissions.ADD_EDIT_ITEMS) && !rowData.systemRule
          ? {
              key: '2',
              label: (
                <Typography.Text style={{ fontSize: '14px', color: colorText }}>
                  {t(`ACHExceptionRule:list.label.editACH`)}
                </Typography.Text>
              ),
              icon: <PenIcon />,
              onClick: () => {
                if (rowData?.ruleId) navigate(`${menuUrls.achExceptionRulesEdit}/${rowData?.ruleId}`)
              },
            }
          : null,
        userPermissions[ESubModules.ACH_EXCEPT_RUL]?.includes(EPermissions.DELETE_ITEMS) && !rowData.systemRule
          ? {
              key: '3',
              label: (
                <Typography.Text style={{ fontSize: '14px', color: colorBgErrorBold }}>
                  {t(`ACHExceptionRule:list.label.deleteACH`)}
                </Typography.Text>
              ),
              icon: <DeleteIcon />,
              onClick: () => {
                if (rowData?.ruleId) {
                  setSelectedACH(rowData)
                  setIsModalConfirmDeleteOpened(true)
                }
              },
            }
          : null,
      ]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps

    [userPermissions, t, navigate],
  )

  const generateCustomColumnDefs = useCallback((columnDefs: Array<TableColumnDefs>): Array<TableColumnDefs> => {
    return columnDefs.map((item: TableColumnDefs, index: any) => {
      if (item.colId === 'amountFrom')
        return {
          ...item,
          width: 150,
          dataType: 'number',
          cellRenderer: (props: any) => {
            const { data } = props
            if (data.amountFrom === null) {
              return ''
            }
            const number = data.amountFrom || 0
            return `${number.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          },
        }

      if (item.colId === 'amountTo')
        return {
          ...item,
          dataType: 'number',
          width: 150,
          cellRenderer: (props: any) => {
            const { data } = props
            if (data.amountTo === null) {
              return ''
            }
            const number = data.amountTo || 0
            return `${number.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          },
        }

      if (item.colId === 'secCodes')
        return {
          ...item,
          width: 150,
          dataType: 'text',
          cellRenderer: (props: any) => {
            const { data } = props
            const columnWidth = props.eGridCell ? props.eGridCell.clientWidth : props.column.getActualWidth()
            return data.secCodes ? (
              <Row style={{ padding: '8px 0px' }}>
                <ShortenListTags
                  data={data.secCodes?.includes('ALL') ? ['ALL SEC CODES'] : data.secCodes}
                  width={columnWidth}
                />
              </Row>
            ) : null
          },
        }

      if (item.colId === 'drcr')
        return {
          ...item,
          width: 100,
          dataType: 'text',
          cellRenderer: (props: any) => {
            const { data } = props

            return data.drcr
          },
        }

      if (item.colId === 'clientNames') {
        return {
          ...item,
          width: 150,
          headerClass: 'custom-header-text',
          cellRenderer: (props: any) => {
            const { data } = props
            const columnWidth = props.eGridCell ? props.eGridCell.clientWidth : props.column.getActualWidth()
            return data.clientNames ? (
              <Row style={{ padding: '8px 0px' }}>
                <ShortenListTags data={data.clientNames} width={columnWidth} />
              </Row>
            ) : null
          },
          isHideInSearchSelect: true,
        }
      }
      if (item.colId === 'accountNames') {
        return {
          ...item,
          width: 150,
          headerClass: 'custom-header-text',
          cellRenderer: (props: any) => {
            const { data } = props
            const columnWidth = props.eGridCell ? props.eGridCell.clientWidth : props.column.getActualWidth()
            return data.accountNames ? (
              <Row style={{ padding: '8px 0px' }}>
                {data.accountNames[0] === 'All Accounts' ? (
                  data.accountNames[0] ?? ''
                ) : (
                  <ShortenListTags data={data.accountNames} width={columnWidth} />
                )}
              </Row>
            ) : null
          },
        }
      }

      if (item.colId === 'achOriginatorID')
        return {
          ...item,
          dataType: 'number',
          width: 150,
          cellRenderer: (props: any) => {
            const { data } = props

            return data.achOriginatorID
          },
        }

      return item
    })
  }, [])

  const handleAddRecord = () => {
    navigate(menuUrls.achExceptionRulesAdd)
  }

  const handleSearch = useCallback(
    async (payload: Partial<any>) => {
      if (currentPayload.current?.filterId) {
        payload.filterId = currentPayload.current?.filterId
      }
      currentPayload.current = payload
      const searchColumns = payload.searchColumns?.filter((item: any) => item !== 'clientNames')
      payload.searchColumns = searchColumns
      await getACHExceptionRuleListData(payload)
      setIsSearchDropdown(false)
    },
    [getACHExceptionRuleListData],
  )

  const staticOption = useCallback(() => {
    return {
      label: t(`ACHExceptionRule:list.label.allClient`),
      value: '',
    }
  }, [t])

  const filterOption = (input: string, option?: { label: string; value: string }) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  const handleClientNameChange = (value: string) => {
    if (currentPayload.current) {
      setClientName(value)
      currentPayload.current.filterId = value
      currentPayload.current.pageIndex = 1
    }
  }

  const handleDeleteACH = async () => {
    if (selectedACH?.ruleId) {
      setIsDeleting(true)
      deleteACHException(selectedACH?.ruleId)
        .then(
          (res: any) => {
            const messageDeleteACH = t('SystemMessages:' + SYSTEM_MESSAGE_IDS.DELETE_ACH_SUCCESSFULLY, {
              Rule_Description: selectedACH?.ruleDescription,
            })
            api.success({ message: messageDeleteACH })

            handleSearch({ ...(currentPayload.current || {}), pageIndex: 1 })
          },
          (err) => {
            console.log('ERROR', err)
          },
        )
        .finally(() => {
          setIsModalConfirmDeleteOpened(false)
          setIsDeleting(false)
        })
    }
  }

  const handleClickSearchByClient = () => {
    if (currentPayload.current) {
      setIsSearchDropdown(true)
      currentPayload.current.searchTerm = ''
      if (!currentPayload.current?.filterId) {
        delete currentPayload.current?.filterId
      }
      handleSearch(currentPayload.current)
    }
  }

  const handleClickRefreshClientSelect = () => {
    setClientName(staticOption().value)
    if (currentPayload.current) {
      setIsSearchDropdown(true)
      delete currentPayload.current.filterId
      currentPayload.current.searchTerm = ''
      currentPayload.current.pageIndex = 1
      handleSearch(currentPayload.current)
    }
  }

  const handleViewErrorResults = () => {
    if (uploadModalRef.current) {
      uploadModalRef.current?.closeModal()
    }
    setIsOpenUploadModal(false)

    if (errorDetailsUploadModalRef.current) {
      errorDetailsUploadModalRef.current?.openModal()
    }
  }

  const handleCloseErrorDetailsUpload = () => {
    if (uploadModalRef.current) {
      uploadModalRef.current?.openModal()
    }
    setIsOpenUploadModal(true)
  }

  useEffect(() => {
    getClientOptions()
  }, [])

  useEffect(() => {
    if (clientOptions.length === 1) {
      setClientName(clientOptions[0].value)
    }
  }, [clientOptions])

  useEffect(() => {
    if (isResetView) {
      handleSearch({})
    }
  }, [isResetView])

  const confirmBulkDeletion = useCallback(() => {
    if (outerTableRef.current?.getSelectedRows) {
      const items = outerTableRef.current?.getSelectedRows() ?? []
      setSelectedRows([...items])
      setIsConfirmBulkDeletionModalOpened(true)
    }
  }, [])

  const handleBulkDeleteRecord = useCallback(async () => {
    try {
      setIsDeleting(true)
      if (selectedRows && selectedRows.length > 0) {
        const ids = selectedRows.map((row: any) => row.ruleId)
        await bulkDeleteACHExceptionRuleList(ids)
        await outerTableRef.current?.handleSearchLogic?.({})
        api.success({
          message: '',
          description:
            t('common:bulk_delete:success_message', {
              count: selectedRows?.length ?? 0,
              type: 'ACH exception rule',
            }) || '',
        })
      }
    } catch (error: any) {
      api.error({
        message: error?.message ?? t('common:error'),
      })
    } finally {
      setIsDeleting(false)
      setSelectedRows([])
      setIsConfirmBulkDeletionModalOpened(false)
    }
  }, [api, setIsDeleting, t, selectedRows])

  const handleDownloadFunc = async (payload: any) => {
    return await getACHExceptionRuleList({
      ...payload,
      filterId: currentPayload.current?.filterId,
    })
  }

  return (
    <>
      <Flex className='landing-ach-exception-rules'>
        <Layout.Content style={{ padding: 24 }}>
          {contextHolder}

          <Form className='form-container' layout='vertical' style={{ padding: 0 }}>
            <Space size={12}>
              <Form.Item label={t(`ACHExceptionRule:list.label.clientName`)}>
                <Select
                  style={{ width: 332 }}
                  showSearch
                  optionFilterProp='children'
                  filterOption={filterOption}
                  options={clientOptions.length === 1 ? clientOptions : [staticOption(), ...clientOptions]}
                  defaultValue={clientOptions.length === 1 ? clientOptions[0].value : ''}
                  onChange={handleClientNameChange}
                  value={clientName}
                  disabled={isLoading}
                />
              </Form.Item>

              <Button
                id='search-button'
                icon={<WhiteSearchIcon />}
                type={'primary'}
                size={'large'}
                style={{ marginLeft: 12 }}
                onClick={handleClickSearchByClient}
                disabled={isLoading}
              />
              <Button
                id='search-button'
                icon={<RefreshIcon />}
                size={'large'}
                style={{ border: `1px solid ${colorBorder}` }}
                onClick={handleClickRefreshClientSelect}
                disabled={isLoading}
              />
            </Space>
          </Form>

          <NewCommonTable
            ref={outerTableRef}
            loading={isLoading}
            rowData={ACHExceptionRuleList}
            total={totalRecords}
            handleAddRecord={handleAddRecord}
            displayAddBtn={userPermissions[ESubModules.ACH_EXCEPT_RUL]?.includes(EPermissions.ADD_ACH_EXCEPT_RUL)}
            actionDropdownItems={getItems}
            handleSearch={handleSearch}
            handleDownload={async (payload: any) => await handleDownloadFunc(payload)}
            getDownloadStatus={getACHExceptionRuleDownloadStatus}
            getDownloadFile={getACHExceptionRuleDownloadFile}
            pageName={TABLE_NAME.ACH_EXCEPTION_RULE}
            displayUploadButton={userPermissions[ESubModules.ACH_EXCEPT_RUL]?.includes(EPermissions.ADD_ACH_EXCEPT_RUL)}
            handleUpload={() => setIsOpenUploadModal(true)}
            translationNamespace='ACHExceptionRule:list.tableColumns'
            tableSettingName={TABLE_NAME.ACH_EXCEPTION_RULE}
            generateCustomColumnDefs={generateCustomColumnDefs}
            isSearchDropdown={isSearchDropdown}
            settingHeightTable={settingHeightTableWithFilter}
            displayBulkDeleteBtn
            handleBulkDeleteRecord={confirmBulkDeletion}
          />

          <ActionConfirmationModal
            icon={<img src={DeleteImg} alt='Confirm Icon' />}
            title={t('Modal:delete_record_title')}
            isOpen={isModalConfirmDeleteOpened}
            description={
              <RichTextField
                richText={
                  t('ACHExceptionRule:list.label.delete_ACH_confirm_description', {
                    Client_Name: selectedACH.clientNames ? selectedACH.clientNames.join(', ') : '',
                  }) || ''
                }
              />
            }
            descriptionType='string'
            onClose={() => {
              setIsModalConfirmDeleteOpened(false)
            }}
            footer={[
              <Button
                size='middle'
                type='primary'
                key='btn-cancel'
                onClick={() => {
                  setIsModalConfirmDeleteOpened(false)
                }}
              >
                {t('common:cancel')}
              </Button>,
              <DeleteButton
                key='btn-delete'
                loading={isLoadingDeleteACH}
                disabled={isLoadingDeleteACH}
                onClick={handleDeleteACH}
              >
                {t('common:delete')}
              </DeleteButton>,
            ]}
          />

          <ACHUploadTemplateModal
            isOpen={isOpenUploadModal}
            onClose={() => setIsOpenUploadModal(false)}
            ref={uploadModalRef}
            onViewErrorResults={handleViewErrorResults}
            setErrorRows={setErrorRows}
            setIsResetView={setIsResetView}
          />
          <ErrorDetailsUpload
            ref={errorDetailsUploadModalRef}
            onClose={handleCloseErrorDetailsUpload}
            errorRows={errorRows}
          />
        </Layout.Content>
      </Flex>
      <ActionConfirmationModal
        icon={<img src={DeleteImg} alt='Confirm Icon' />}
        title={t('common:bulk_delete:record_title')}
        isOpen={isConfirmBulkDeletionModalOpened}
        description={
          <RichTextField
            richText={
              t('common:bulk_delete:confirm_description', {
                count: selectedRows?.length ?? 0,
              }) || ''
            }
          />
        }
        descriptionType='string'
        onClose={() => {
          setSelectedRows([])
          setIsConfirmBulkDeletionModalOpened(false)
        }}
        footer={[
          <Button
            size='middle'
            type='primary'
            key='btn-cancel'
            onClick={() => {
              setSelectedRows([])
              setIsConfirmBulkDeletionModalOpened(false)
            }}
          >
            {t('common:cancel')}
          </Button>,
          <DeleteButton key='btn-delete' loading={isDeleting} disabled={isDeleting} onClick={handleBulkDeleteRecord}>
            {t('common:delete')}
          </DeleteButton>,
        ]}
      />
    </>
  )
}

export default LandingACHExceptionRules
